/**
 * ColumnsPanel — right-side drawer to manage column visibility / order / width.
 *
 * UX rules implemented here:
 *  • Each row is draggable; a visible "grip" handle on the left signals it
 *    and dragOver feedback shows where the row will land.
 *  • The number input on the right is labelled `px` to make clear it's
 *    the column width — not the order. The order is dictated by drag.
 *  • Click the eye to toggle visibility; hidden columns dim + dashed border.
 */
import { useState } from "react";
import { X, Eye, EyeOff, GripVertical } from "lucide-react";
import { useI18n } from "../context/I18nContext";

export default function ColumnsPanel({ open, onClose, columns, onChange, availableCatalog = [], onAddFromCatalog }) {
  const { t } = useI18n();
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  if (!open) return null;

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx !== idx) setOverIdx(idx);
  };
  const onDragLeave = () => setOverIdx(null);
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    setOverIdx(null);
    if (dragIdx == null || dragIdx === idx) return;
    const next = [...columns];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    onChange(next);
    setDragIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const toggle = (i) => {
    const next = [...columns];
    next[i] = { ...next[i], visible: !next[i].visible };
    onChange(next);
  };
  const setWidth = (i, w) => {
    const next = [...columns];
    next[i] = { ...next[i], width: Math.max(50, Number(w) || 100) };
    onChange(next);
  };

  const catalogSet = new Set(columns.map((c) => c.key));
  const catalogExtras = availableCatalog.filter((c) => !catalogSet.has(c.key));

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="columns-panel">
      <div className="flex-1 bg-zinc-950/30" onClick={onClose} />
      <aside className="w-full max-w-md bg-white border-l border-zinc-200 shadow-2xl flex flex-col animate-fade-in">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="cs-label">{t("columns_panel_section")}</div>
            <h3 className="font-heading text-lg font-semibold">{t("columns")}</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5 font-mono">{t("columns_drag_hint")}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100" data-testid="columns-close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-1 scrollbar-thin" onDragLeave={onDragLeave}>
          {columns.map((c, i) => (
            <div
              key={c.key}
              draggable
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDrop={onDrop(i)}
              onDragEnd={onDragEnd}
              className={`flex items-center gap-2 px-2 py-2 rounded-md border transition-colors
                ${c.visible ? "border-zinc-200 bg-white" : "border-dashed border-zinc-200 bg-zinc-50 opacity-70"}
                ${dragIdx === i ? "opacity-40" : ""}
                ${overIdx === i && dragIdx !== null && dragIdx !== i ? "border-cs-accent border-t-2 bg-cs-accent/5" : ""}`}
              data-testid={`column-row-${c.key}`}
            >
              <div
                className="p-1 -m-1 cursor-grab active:cursor-grabbing text-zinc-400 hover:text-cs-accent shrink-0"
                title={t("columns_drag_handle_title")}
                data-testid={`column-handle-${c.key}`}
              >
                <GripVertical size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-zinc-900 truncate">{c.label}</div>
                <div className="text-[10px] font-mono text-zinc-400 truncate">{c.key} · {c.type}</div>
              </div>
              <label className="flex items-center gap-1 shrink-0" title={t("columns_width_px_title")}>
                <input
                  type="number"
                  value={c.width || 120}
                  onChange={(e) => setWidth(i, e.target.value)}
                  className="w-14 px-1.5 py-0.5 border border-zinc-200 rounded text-[10px] font-mono text-right"
                  data-testid={`column-width-${c.key}`}
                  step={10}
                  min={50}
                />
                <span className="text-[9px] font-mono text-zinc-400 uppercase">px</span>
              </label>
              <button onClick={() => toggle(i)} className="p-1 rounded hover:bg-zinc-100 shrink-0" data-testid={`column-toggle-${c.key}`}>
                {c.visible ? <Eye size={14} className="text-cs-accent" /> : <EyeOff size={14} className="text-zinc-400" />}
              </button>
            </div>
          ))}
          {catalogExtras.length > 0 && (
            <div className="pt-4 mt-4 border-t border-zinc-100">
              <div className="cs-label mb-2">{t("columns_available_to_add")}</div>
              <div className="flex flex-wrap gap-1.5">
                {catalogExtras.map((c) => (
                  <button
                    key={c.key}
                    onClick={() => onAddFromCatalog?.(c)}
                    className="px-2 py-1 border border-dashed border-zinc-300 rounded text-[11px] hover:border-cs-accent hover:text-cs-accent"
                    data-testid={`column-add-${c.key}`}
                  >
                    + {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
